import { useState, useEffect } from "react";
import {
  getOrganisation,
  getTrainers,
} from "../../services/training-session.service";
import { CryptoMethods } from "../../utils/crypto-methods";
import { useDebouncedCallback } from "use-debounce";
interface TrainerItem {
  code: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
}

export interface OrganisationItem {
  code: string;
  contactDetails: string;
  contactEmail: string;
  createdAt: string;
  deletedAt: any;
  id: number;
  isActive: boolean;
  isSelfApprover: false;
  name: string;
}

const fetchTrainers = async (
  trainerType: string,
  search: string,
  getAllData = false
) => {
  try {
    let encryptedSearch = "";
    if (search) {
      encryptedSearch = await CryptoMethods.ecbEncryptData(search);
    }
    const trainers = await getTrainers(trainerType, encryptedSearch);
    if (getAllData) {
      return trainers;
    }
    return trainers.map((item: TrainerItem) => ({
      label: `${item.firstName} ${item.lastName}`,
      value: `${item.id}`,
    }));
  } catch (error) {
    console.error("Error fetching trainers:", error);
    return [];
  }
};

export const useTrainerOptions = (trainerType: string, getAllData = false) => {
  const [options, setOptions] = useState([]);
  const [isUploadloading, setIsUploadloading] = useState(false);

  const fetchOptions = async (search = "") => {
    setIsUploadloading(true);
    const formattedOptions = await fetchTrainers(
      trainerType,
      search,
      getAllData
    );
    setOptions(formattedOptions);
    setIsUploadloading(false);
  };

  const debouncedFetchOptions = useDebouncedCallback(fetchOptions, 500);
  useEffect(() => {
    fetchOptions();
  }, [trainerType]);

  return { options, isUploadloading, fetchOptions: debouncedFetchOptions };
};

export const useOrgOptions = (colabOrg: number) => {
  const [options, setOptions] = useState([]);
  const [isUploadloading, setIsUploadloading] = useState(false);

    const fetchOrganisation = async (search: string) => {
      try {
        setIsUploadloading(true);
        const orderBy = "ASC";
        const sortBy = "name";
        const trainers = await getOrganisation(
          colabOrg,
          search,
          orderBy,
          sortBy
        );
        const formattedOptions = trainers.map((item: OrganisationItem) => ({
          label: `${item.name}`,
          value: `${item.id}`,
        }));
        setOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching trainers:", error);
        setOptions([]);
      } finally {
        setIsUploadloading(false);
      }
    };
  const debouncedFetchOptions = useDebouncedCallback(fetchOrganisation, 500);

  useEffect(() => {
    fetchOrganisation("");
  }, [colabOrg]);

  return {options, isUploadloading, fetchOrganisation: debouncedFetchOptions};
};

export function mergeSelectedIntoOptions(options: any[], selected: any[]): any[] {
  const selectedValues = new Set(selected.map((s) => s.value));
  const filteredOptions = options.filter((opt) => !selectedValues.has(opt.value));
  return [...selected, ...filteredOptions];
}

export function mergeSelectedWithList<T extends Record<string, any>>(
  list: T[],
  selected: T | null,
  key: keyof T
): T[] {
  if (!selected) return list;

  const exists = list.some((item) => item[key] === selected[key]);
  const merged = exists ? list : [selected, ...list];

  return merged.reduce((acc: T[], curr) => {
    if (!acc.find((item) => item[key] === curr[key])) {
      acc.push(curr);
    }
    return acc;
  }, []);
}

